import { useCallback, useEffect, useState } from 'react';
import { getWhitelistedEmails } from 'Api/uploaderApiFunctions';

const emit = () => {
  try {
    window.dispatchEvent(new Event('newUIFlagChange'));
  } catch {
    /* noop */
  }
};

const FLAG_KEY = 'newUIEnabled';
const PROMPT_KEY = 'newUIPromptShown';
const THEME_KEY = 'newUITheme';
const EMAIL_KEY = 'userEmail';
// Session-scoped marker for the onboarding lane in progress. sessionStorage,
// not localStorage: a lane belongs to the tab that started it and must not
// leak into the next visit on the same browser.
const LANE_KEY = 'newUILane';
// Cached copy of the server-side allow-list.
const ALLOWED_EMAILS_KEY = 'newUIAllowedEmails';

// The allow-list now comes from the upload service (see refreshAllowedEmails
// below) and is cached here. This seed is only a cold-start fallback: it is used
// until the first successful fetch, so an allow-listed user isn't dropped to the
// classic UI on a fresh browser or while the API is unreachable. Keep entries
// lowercase.
export const NEW_UI_ALLOWED_EMAILS = ['layne@projector.app', 'jia6@yopmail.com'];

const readAllowedEmails = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(ALLOWED_EMAILS_KEY));
    // An empty array is a real answer ("nobody"), so only fall back to the seed
    // when there is no usable cache at all.
    if (Array.isArray(cached)) return cached;
  } catch {
    /* unparseable cache — fall through to the seed */
  }
  return NEW_UI_ALLOWED_EMAILS;
};

// Is a given email allowed to use the new UI?
export const canUseNewUI = (email) => readAllowedEmails()
  .includes((email || '').trim().toLowerCase());

/**
 * Pull the allow-list and cache it. Safe to call on every app start: a failure
 * leaves the previous cache in place rather than locking anyone out, and the
 * gate stays synchronous because everything reads the cache, never the network.
 */
export const refreshAllowedEmails = async () => {
  try {
    const emails = await getWhitelistedEmails();
    localStorage.setItem(ALLOWED_EMAILS_KEY, JSON.stringify(emails));
    emit();
  } catch {
    /* keep whatever is cached */
  }
};

// Reads the email persisted by getMyProfile (app.jsx) so the gate works
// synchronously in the non-reactive isNewUIEnabled() call.
const emailAllowed = () => {
  try {
    return canUseNewUI(localStorage.getItem(EMAIL_KEY));
  } catch {
    return false;
  }
};

const isSignedIn = () => {
  try {
    return !!localStorage.getItem('token');
  } catch {
    return false;
  }
};

// The preference is stored per account. Two people share a browser far more
// often than they share an opinion about the layout, and an allow-listed user
// signing out must not leave the new UI switched on for whoever signs in next.
const prefKey = (email) => `${FLAG_KEY}:${(email || '').trim().toLowerCase()}`;
const readStoredPref = () => {
  try {
    const perAccount = localStorage.getItem(prefKey(localStorage.getItem(EMAIL_KEY)));
    // Fall back to the old single-key flag once, so an allow-listed user who
    // already opted in does not get reset by this change.
    return (perAccount !== null ? perAccount : localStorage.getItem(FLAG_KEY)) === 'true';
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Onboarding lanes
//
// Both lanes start on /setpassword and are told apart exactly the way the page
// itself does it (set-password/index.jsx): `gift=true` is the gift recipient,
// anything else is a quick-user link. They outrank the allow-list in both
// directions, because the person following the link has no account yet — there
// is no email to check.
// ---------------------------------------------------------------------------
const SET_PASSWORD_PATH = '/setpassword';
const GIFT_PATH_PREFIXES = ['/gift', '/receiving-gift'];

const readLane = () => {
  try {
    return sessionStorage.getItem(LANE_KEY) || '';
  } catch {
    return '';
  }
};

/** Gift recipients stay on the classic UI for the whole lane. */
const inGiftLane = () => {
  try {
    const { pathname, search } = window.location;
    if (GIFT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
    if (pathname.startsWith(SET_PASSWORD_PATH)) {
      return new URLSearchParams(search).get('gift') === 'true';
    }
    return readLane() === 'gift';
  } catch {
    return false;
  }
};

/**
 * Quick-user links get the new UI whoever follows them. Read from the URL
 * rather than the marker on the first page, so the very first paint is already
 * correct — the marker only has to carry the lane through the steps that
 * follow (avatar → interview → quick-user-payment), whose URLs say nothing
 * about where the user came from.
 */
const inQuickUserLane = () => {
  try {
    const { pathname, search } = window.location;
    if (pathname.startsWith(SET_PASSWORD_PATH)) {
      return new URLSearchParams(search).get('gift') !== 'true';
    }
    return readLane() === 'quick';
  } catch {
    return false;
  }
};

/** Remembers which lane this tab is in, for the steps after /setpassword. */
export const markUiLane = (lane) => {
  try {
    if (lane) sessionStorage.setItem(LANE_KEY, lane);
    else sessionStorage.removeItem(LANE_KEY);
    emit();
  } catch {
    /* noop */
  }
};

export const clearUiLane = () => markUiLane('');

// Classic (production) UI is the default, and signed-out visitors always get it
// — the login page has to look the same for everyone, since we cannot know who
// is about to sign in. Past that, an allow-listed user's own stored preference
// decides. The two onboarding lanes are the exceptions, and are checked first.
const readFlag = () => {
  try {
    if (inGiftLane()) return false;
    if (inQuickUserLane()) return true;
    if (!isSignedIn()) return false;
    if (!emailAllowed()) return false;
    return readStoredPref();
  } catch {
    return false;
  }
};

const readPromptShown = () => {
  try {
    return localStorage.getItem(PROMPT_KEY) === 'true';
  } catch {
    return true;
  }
};

const readTheme = () => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

export const isNewUIEnabled = () => readFlag();

export const setNewUIEnabled = (val) => {
  try {
    localStorage.setItem(prefKey(localStorage.getItem(EMAIL_KEY)), val ? 'true' : 'false');
    // Drop the legacy shared key so it cannot bleed into another account.
    localStorage.removeItem(FLAG_KEY);
    localStorage.setItem(PROMPT_KEY, 'true');
    emit();
  } catch {
    /* noop */
  }
};

export const markPromptShown = () => {
  try {
    localStorage.setItem(PROMPT_KEY, 'true');
    emit();
  } catch {
    /* noop */
  }
};

export const setNewUITheme = (mode) => {
  try {
    localStorage.setItem(THEME_KEY, mode === 'dark' ? 'dark' : 'light');
    emit();
  } catch {
    /* noop */
  }
};

export const useNewUI = () => {
  const [enabled, setEnabled] = useState(readFlag());
  const [promptShown, setPromptShownState] = useState(readPromptShown());
  const [themeMode, setThemeModeState] = useState(readTheme());

  useEffect(() => {
    const sync = () => {
      setEnabled(readFlag());
      setPromptShownState(readPromptShown());
      setThemeModeState(readTheme());
    };
    window.addEventListener('newUIFlagChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('newUIFlagChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const enable = useCallback(() => setNewUIEnabled(true), []);
  const disable = useCallback(() => setNewUIEnabled(false), []);
  const dismissPrompt = useCallback(() => markPromptShown(), []);
  const toggleTheme = useCallback(() => {
    setNewUITheme(readTheme() === 'dark' ? 'light' : 'dark');
  }, []);
  const setTheme = useCallback((mode) => setNewUITheme(mode), []);

  return {
    enabled,
    promptShown,
    themeMode,
    enable,
    disable,
    dismissPrompt,
    toggleTheme,
    setTheme,
  };
};
